import { useReducer } from "react"
import {
	MFA_API_ERROR,
	useEnableAuthenticatorMutation,
	useReauthenticatePasswordMutation,
	useSetupAuthenticatorMutation,
} from "@api/mfa-api.ts"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import type { RestFetchError } from "@/types/common"

// Enums are disabled so object is used.
const MultiFactorSetupStep = {
	REAUTH: 0,
	SETUP: 1,
	QR_CODE: 2,
	INPUT_OTP_CODE: 3,
	FINALIZE: 4,
}

interface MfaSetupSetup {
	step: number

	// User inputted
	password: string
	otpCode: string

	// Responses
	uri: string | null
	qrCode: string | null
	secret: string | null
	recoveryCodes: string[] | null
}

const initialState: MfaSetupSetup = {
	step: MultiFactorSetupStep.REAUTH,
	password: "",
	otpCode: "",

	uri: null,
	qrCode: null,
	secret: null,
	recoveryCodes: null,
}

type MfaAction =
	| { type: "SET_REAUTH_PASSWORD"; payload: { password: string } }
	| { type: "SET_OTP_CODE"; payload: { otpCode: string } }
	| { type: "CLEAR_REAUTH_PASSWORD" }
	| { type: "REAUTH_DONE" }
	| { type: "SETUP_DONE"; payload: { uri: string; qrCode: string; secret: string } }
	| { type: "ENABLED"; payload: { recoveryCodes: string[] } }
	| { type: "QR_CODE_DONE" }
	| { type: "RESET" }

function mfaSetupReducer(state: MfaSetupSetup, action: MfaAction): MfaSetupSetup {
	switch (action.type) {
		case "SET_REAUTH_PASSWORD":
			return { ...state, password: action.payload.password }
		case "SET_OTP_CODE":
			return { ...state, otpCode: action.payload.otpCode }
		case "CLEAR_REAUTH_PASSWORD":
			return { ...state, password: "" }
		case "REAUTH_DONE":
			return { ...state, password: "", step: MultiFactorSetupStep.SETUP }
		case "SETUP_DONE":
			return { ...state, ...action.payload, step: MultiFactorSetupStep.QR_CODE }
		case "ENABLED":
			return { ...state, recoveryCodes: action.payload.recoveryCodes, step: MultiFactorSetupStep.FINALIZE }
		case "QR_CODE_DONE":
			return { ...state, step: MultiFactorSetupStep.INPUT_OTP_CODE }
		case "RESET":
			return initialState
		default:
			return state
	}
}

export default function useMfaSetup() {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [triggerReAuth, { isLoading: isLoadingReAuth }] = useReauthenticatePasswordMutation()
	const [triggerSetup, { isLoading: isLoadingSetup }] = useSetupAuthenticatorMutation()
	const [triggerEnable, { isLoading: isLoadingEnable }] = useEnableAuthenticatorMutation()

	const [state, dispatch] = useReducer(mfaSetupReducer, initialState)
	const { password, otpCode, recoveryCodes, step } = state

	const isLoading = isLoadingReAuth || isLoadingSetup || isLoadingEnable

	async function verifyReAuthPassword() {
		if (password === "") {
			toast.error(t("Salasana on annettava"))
			return
		}
		dispatch({ type: "CLEAR_REAUTH_PASSWORD" })

		try {
			await triggerReAuth({ password, purpose: "setup_mfa" }).unwrap()
			dispatch({ type: "REAUTH_DONE" })
		} catch (err) {
			handleFetchError(err as RestFetchError)
		}
	}

	async function setupAuthenticator() {
		try {
			const result = await triggerSetup().unwrap()
			dispatch({
				type: "SETUP_DONE",
				payload: {
					uri: result.uri,
					qrCode: result.qrCode,
					secret: result.secret,
				},
			})
		} catch (err) {
			handleFetchError(err as RestFetchError)
		}
	}

	async function enableAuthenticator() {
		if (otpCode.length < 6) {
			toast.error(t("Kirjoita tunnuslukusovelluksen antama koodi"))
			return
		}
		try {
			const result = await triggerEnable({ code: otpCode }).unwrap()
			dispatch({
				type: "ENABLED",
				payload: {
					recoveryCodes: result.recoveryCodes,
				},
			})

			toast.success(t("Monivaiheinen tunnistautuminen on otettu käyttöön."))
		} catch (err) {
			handleFetchError(err as RestFetchError)
		}
	}

	function handleFetchError(err: RestFetchError) {
		const { code } = err.data
		if (code === MFA_API_ERROR.INVALID_CREDENTIALS) {
			toast.error(t("Väärä salasana."))
			return
		}
		if (code === MFA_API_ERROR.MFA_INVALID_CODE) {
			toast.error(t("Väärä koodi."))
			return
		}
		if (code === MFA_API_ERROR.FEATURE_NOT_AVAILABLE) {
			toast.error(t("Ominaisuus ei ole tuettu nykyisellä käyttäjällä."))
			return
		}
		if (code === MFA_API_ERROR.MFA_SESSION_EXPIRED || code === MFA_API_ERROR.RE_AUTH_INVALID) {
			toast.error(t("Uudelleen kirjautuminen on vanhentunut. Aloita käyttöönotto uudelleen."))
			return
		}
		toast.error(t("Jotain meni vikaan."))
	}

	function secretScanned() {
		dispatch({ type: "QR_CODE_DONE" })
	}

	const downloadRecoveryCodes = () => {
		const fileContent = (recoveryCodes || []).join("\n")
		const blob = new Blob([fileContent], {
			type: "text/plain",
		})
		const link = document.createElement("a")

		link.href = URL.createObjectURL(blob)
		link.download = "recovery_codes.txt"

		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)

		URL.revokeObjectURL(link.href)
	}

	const onPasswordChange = (password: string) => dispatch({ type: "SET_REAUTH_PASSWORD", payload: { password } })
	const onOtpCodeChange = (otpCode: string) => dispatch({ type: "SET_OTP_CODE", payload: { otpCode } })

	async function onNextStep() {
		switch (step) {
			case MultiFactorSetupStep.REAUTH:
				await verifyReAuthPassword()
				break
			case MultiFactorSetupStep.SETUP:
				await setupAuthenticator()
				break
			case MultiFactorSetupStep.QR_CODE:
				secretScanned()
				break
			case MultiFactorSetupStep.INPUT_OTP_CODE:
				await enableAuthenticator()
				break
			case MultiFactorSetupStep.FINALIZE:
				navigate("/settings")
				break
			default:
		}
	}

	function getNextStepButtonLabel() {
		switch (step) {
			case MultiFactorSetupStep.REAUTH:
				return t("Vahvista")
			case MultiFactorSetupStep.SETUP:
				return t("Ota käyttöön")
			case MultiFactorSetupStep.QR_CODE:
				return t("Seuraava")
			case MultiFactorSetupStep.INPUT_OTP_CODE:
				return t("Ota käyttöön")
			case MultiFactorSetupStep.FINALIZE:
				return t("Olen ladannut palautuskoodit, jatka")
			default:
		}
	}

	const isLastStep = step === MultiFactorSetupStep.FINALIZE

	return {
		// Methods
		// verifyReAuthPassword,
		// setupAuthenticator,
		// enableAuthenticator,
		downloadRecoveryCodes,
		onPasswordChange,
		onOtpCodeChange,
		onNextStep,
		getNextStepButtonLabel,

		// State
		isLoading,
		isLastStep,
		...state,
	}
}
